// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DeliveryInventoryMessage from "@bg/App/Components/OnePageCheckout/DeliveryInventoryMessage";
import { CustomAppState } from "@bg/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import OnePageCheckoutState from "@insite/client-framework/Store/Pages/OnePageCheckout/OnePageCheckoutState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";

interface OwnProps {
    cart?: Cart;
    updateShippingAddressFormStateSource: (
        newShippingAddressFormState: OnePageCheckoutState["shippingAddressFormState"],
    ) => void;
}

const mapStateToProps = (state: CustomAppState, { cart }: OwnProps) => {
    return {
        shippingAddressFormState: state.pages.onePageCheckout.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.onePageCheckout.isShippingAddressUpdateRequired,
        shipTo: getShipToState(state, cart?.shipToId)?.value,
        useOneTimeAddress: state.xOnePageCheckout.useOneTimeAddress,
        isShippingAddressEditing: state.xOnePageCheckout.isShippingAddressEditing,
        isBillingAddressEditing: state.xOnePageCheckout.isBillingAddressEditing,
    };
};

const mapDispatchToProps = {
    setIsShippingAddressEditing: (isEditing: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xOnePageCheckout/IsShippingAddressEditing" as any,
            isEditing,
        });
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface ShippingAddressInfoStyles {
    container?: InjectableCss;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    editAddressLink?: LinkPresentationProps;
}

export const shippingAddressInfoStyles: ShippingAddressInfoStyles = {};

const styles = shippingAddressInfoStyles;
const ShippingAddressInfo: FC<Props> = ({
    cart,
    shipTo,
    isShippingAddressUpdateRequired,
    isShippingAddressEditing,
    isBillingAddressEditing,
    useOneTimeAddress,
    shippingAddressFormState,
    setIsShippingAddressEditing,
    updateShippingAddressFormStateSource,
}) => {
    const oneTimeEditAddressOpen = () => {
        if (isBillingAddressEditing) {
            return;
        }

        updateShippingAddressFormStateSource(shippingAddressFormState);
        setIsShippingAddressEditing(true);
    };

    if (!shipTo || isShippingAddressUpdateRequired || isShippingAddressEditing) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="OnePageCheckoutShippingAddressInfo">
            <AddressInfoDisplay
                {...shipTo}
                state={shipTo.state ? shipTo.state.abbreviation : undefined}
                country={shipTo.country ? shipTo.country.abbreviation : undefined}
                extendedStyles={styles.addressInfoDisplay}
            />
            <DeliveryInventoryMessage cart={cart} />
            {useOneTimeAddress && (
                <Link onClick={oneTimeEditAddressOpen} disabled={isBillingAddressEditing} {...styles.editAddressLink}>
                    {translate("Edit Address")}
                </Link>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ShippingAddressInfo);
